import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { useHistory } from 'react-router';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Input, Select, Radio } from 'fpay-ui';

import GooglePlacesAutocomplete from '@components/AddressGoogleBar';
import { GenericForm } from '@components';
import { useAuth, useUser, useZipCode } from '@hooks';
import Button from '@components/Forms/Button';
import { removeAccents } from '@helpers';
import { formFieldsHaveValue } from '@helpers/validator';
import schema from './schema';

const StyledGooglePlacesAutocomplete = styled(GooglePlacesAutocomplete)(
  ({ theme }) => css`
    margin-bottom: 0px;
    & .MuiInputBase-root {
      padding: 13px 15px;
      ${theme.newBreakpoint('sm')} {
        padding: 17px 14px;
      }
    }
    & .MuiInputBase-root input {
      padding: 0;
      color: initial;
      ${theme.textTypes['Body-2-w400']}
    }
    & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
      border-color: ${theme.colors.gray600};
    }
    & + .container-suggestions {
      width: 100%;
    }
    & + .container-suggestions:empty {
      border: none !important; /* !important Es la única forma de sobreescribir los estilos */
    }
  `
);

const StyledSelect = styled(Select)`
  margin-bottom: 10px;
  & > div > div:nth-child(4) {
    z-index: 2;
  }
  & ul {
    max-height: 200px;
    overflow-y: scroll;
  }
  ${(props) =>
    props.value &&
    css`
      & > div > div:nth-child(2) {
        color: initial;
      }
    `}
`;

const StyledRadio = styled(Radio)(
  ({ theme }) => css`
    & span::before {
      box-sizing: initial;
      top: 0;
    }
    & span[disabled]:before {
      border: 1px solid ${theme.colors.gray600};
    }
    & span::after {
      box-sizing: initial;
      top: 4px;
    }
    & span[disabled]:after {
      background: ${theme.colors.gray600};
    }
  `
);

const FormCommerce = ({ edit }) => {
  const { t } = useTranslation();
  const { merchant, updateMerchantProfile } = useAuth();
  const { users } = useUser();
  const userLegalAgent = users.find((el) => el.is_legal_agent);
  const history = useHistory();
  const { zipCodes } = useZipCode();
  const [addressError, setAddressError] = useState(false);
  const [anyChange, setChange] = useState(false);
  const [merchantProfile, setMerchantProfile] = useState(edit ? merchant : {});

  const handleChange = (e) => {
    const { target } = e;
    setChange(true);
    setMerchantProfile({ ...merchantProfile, [target.id]: target.value });
  };

  const handleChangeZipCode = (e) => {
    const { target } = e;
    setChange(true);
    setMerchantProfile({
      ...merchantProfile,
      address: {
        ...merchantProfile.address,
        zip_code: target.value,
      },
    });
  };

  const handleAddressSelected = (value) => {
    const { administrative_area_level_3: comuna } = value;
    if (comuna) {
      setChange(true);
      const comunaZipCode = zipCodes.find(
        (code) => code.comuna_normalize === removeAccents(comuna.toUpperCase())
      );
      setMerchantProfile({
        ...merchantProfile,
        address: {
          ...merchantProfile.address,
          zip_code: comunaZipCode.zip_code,
          ...value,
        },
      });
    }
  };

  const handleSubmit = () => {
    if (addressError || !userLegalAgent) return;
    if (edit) {
      updateMerchantProfile(userLegalAgent._id, merchantProfile);
    }
  };

  const handleCancel = () => {
    history.goBack();
  };

  const getZipCodeLabel = (option) => `${option.zip_code} - ${option.comuna}`;
  const getZipCodeValue = (option) => option.zip_code;

  const isSubmitDisabled =
    !anyChange ||
    formFieldsHaveValue([
      merchantProfile?.merchant_name,
      merchantProfile?.document?.doc_number,
      merchantProfile?.address?.formatted_address,
      merchantProfile?.address?.zip_code,
    ]);
  return (
    <GenericForm
      schema={schema}
      justify="flex-end"
      item
      xs={12}
      container
      spacing={1}
      onSubmit={handleSubmit}
    >
      <Input
        id="merchant_name"
        name="merchant_name"
        testId="merchant_name"
        gridProps={{ xs: 12, item: true }}
        onChange={handleChange}
        value={merchantProfile.merchant_name}
        label={t('common.fantasyName')}
      />
      <Input
        name="rut"
        testId="rut"
        gridProps={{ xs: 12, item: true }}
        onChange={() => {}}
        value={merchantProfile?.document.doc_number}
        label={t('common.docType')}
        disabled={edit}
      />
      <StyledGooglePlacesAutocomplete
        id="formatted_address"
        name="formatted_address"
        textFieldProps={{
          fullWidth: true,
          variant: 'outlined',
          label: t('common.address'),
          name: 'address',
        }}
        country={t('country')}
        gridProps={{ xs: 12, item: true, style: { position: 'relative' } }}
        setAddressError={setAddressError}
        setAddressSelected={handleAddressSelected}
        address={merchantProfile?.address?.formatted_address}
      />
      <Input
        id="address_info"
        name="address_info"
        testId="address_info"
        gridProps={{ xs: 12, item: true }}
        onChange={handleChange}
        value={merchantProfile.address_info}
        label={t('common.addressInfo')}
      />
      <StyledSelect
        name="zip_code"
        testId="zip_code"
        gridProps={{ xs: 12, item: true }}
        options={zipCodes}
        onChange={handleChangeZipCode}
        placeholder={t('common.zipCode')}
        value={merchantProfile?.address.zip_code}
        label={t('common.zipCode')}
        getOptionLabel={getZipCodeLabel}
        getOptionValue={getZipCodeValue}
      />
      <Input
        id="business_name"
        testId="business_name"
        gridProps={{ xs: 12, item: true }}
        onChange={() => {}}
        value={merchantProfile.business_name}
        label={t('common.legalEntity')}
        disabled={edit}
      />
      <StyledRadio
        name="one_day_company"
        testId="one_day_company"
        gridProps={{ xs: 12, item: true }}
        options={[
          { value: '1', label: t('common.yes') },
          { value: '0', label: t('common.no') },
        ]}
        onChange={() => {}}
        value={merchantProfile.one_day_company ? '1' : '0'}
        label={t('common.oneDayCompany')}
        disabled
      />

      <>
        <Button
          style={{ maxWidth: '102px', marginRight: '16px' }}
          onClick={handleCancel}
          variant="outlined"
          data-testid="formcommerce-cancel-button"
        >
          {t('commerceData.editCommerce.cancel')}
        </Button>
        <Button
          type="submit"
          data-testid="formcommerce-save-button"
          style={{ maxWidth: '102px' }}
          disabled={isSubmitDisabled}
        >
          {t('commerceData.editCommerce.save')}
        </Button>
      </>
    </GenericForm>
  );
};

FormCommerce.propsTypes = {
  edit: PropTypes.bool,
};

FormCommerce.defaultProps = {
  edit: false,
};

export default FormCommerce;
